import { SuburbRegion, SuburbSubRegion } from '../../../../services/suburb-areas.service';
import { MapRegion, MapSubRegion, Rgb } from '../map/map-types';

/**
 * Region and sub-region colours for the map (#256, #260): the theme's tokens --app-region-1..8 (by display order) and
 * --app-subregion-1..8 (a sub-region's chosen slot, else the next in its region), read once and handed to the engine
 * as RGB, since deck.gl can't use CSS. Pure but for `cssToken`; tested in map-colours.spec.ts.
 */

/** The eight colour slots each token family has. */
export const COLOUR_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8];
/** Used only if a token is missing (a theme that predates it): slate for a region, ink for a sub-region. */
const REGION_FALLBACK = '#64748b';
const SUBREGION_FALLBACK = '#0f172a';

/** Reads a CSS custom property from the document's theme. */
export type TokenReader = (name: string) => string;

export function cssToken(): TokenReader {
  const css = getComputedStyle(document.documentElement);
  return (name) => css.getPropertyValue(name);
}

/** '#3b82f6' (or '#3b8') to [59, 130, 246]; short or odd input is padded with zeros, as it always was. */
export function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbCss(rgb: Rgb): string {
  return `rgb(${rgb.join(',')})`;
}

/** A sub-region's colour slot: the one saved on its record when valid (1–8), else the next in its region's list. */
export function colourSlot(mapColour: number | null | undefined, fallbackIndex: number): number {
  if (
    typeof mapColour === 'number' &&
    Number.isInteger(mapColour) &&
    mapColour >= 1 &&
    mapColour <= COLOUR_SLOTS.length
  )
    return mapColour;
  return (fallbackIndex % COLOUR_SLOTS.length) + 1;
}

/** The first slot none of these sub-regions (one region's, in order) uses; slot 1 when all are taken. */
export function freeSlot(subRegions: Array<{ mapColour?: number | null }>): number {
  const used = new Set(subRegions.map((s, i) => colourSlot(s.mapColour, i)));
  return COLOUR_SLOTS.find((n) => !used.has(n)) ?? 1;
}

export function mapRegions(regions: SuburbRegion[], token: TokenReader): MapRegion[] {
  return regions.map((r, i) => {
    const hex = token(`--app-region-${(i % COLOUR_SLOTS.length) + 1}`).trim() || REGION_FALLBACK;
    return { id: r.id, name: r.name, state: r.state, rgb: hexToRgb(hex) };
  });
}

export function mapSubRegions(refs: SuburbSubRegion[], token: TokenReader): MapSubRegion[] {
  const nth = new Map<string, number>();
  return refs.map((s) => {
    const i = nth.get(s.regionId) ?? 0;
    nth.set(s.regionId, i + 1);
    const hex = token(`--app-subregion-${colourSlot(s.mapColour, i)}`).trim() || SUBREGION_FALLBACK;
    return { id: s.id, name: s.name, regionId: s.regionId, rgb: hexToRgb(hex) };
  });
}
